import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import * as reportService from '../services/reportService';

// Report interfaces
export interface ReportFilters {
  start_date?: string;
  end_date?: string;
  report_type?: string;
  filters?: {
    school_id?: string;
    area_id?: string;
    student_id?: string;
  };
}

export interface ReportData {
  id: string;
  type: string;
  title: string;
  generated_at: string;
  period: {
    start: string;
    end: string;
  };
  metrics: {
    total_activities: number;
    total_incidents: number;
    active_students: number;
    attendance_rate: number;
  };
  data?: Record<string, unknown>;
}

export interface ExportOptions {
  format: 'pdf' | 'excel' | 'csv';
  include_charts?: boolean;
  include_raw_data?: boolean;
}


// Hooks
export const useReports = (filters: ReportFilters) => {
  return useQuery({
    queryKey: ['reports', filters],
    queryFn: async () => {
      const response = await reportService.getReports({
        date_from: filters.start_date,
        date_to: filters.end_date,
        type: filters.report_type,
      });
      return response;
    },
    select: (data) => {
      // API returns { reports: Report[], pagination: Pagination }
      // Return consistent structure with reports array
      return {
        reports: data.reports || [],
        pagination: data.pagination || null
      };
    },
    refetchInterval: 60000, // Refetch every minute
  });
};

export const useGenerateReport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (options: {
      type: string;
      filters: ReportFilters;
      schedule?: 'now' | 'daily' | 'weekly' | 'monthly';
    }) => {
      const response = await reportService.generateReport({
        type: options.type,
        date_range: {
          start: options.filters.start_date || '',
          end: options.filters.end_date || ''
        },
        filters: options.filters.filters
      });
      return response;
    },
    onSuccess: (_data) => {
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      notifications.show({
        title: 'Report Generated',
        message: `Report has been generated successfully`,
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Generation Failed',
        message: 'Failed to generate report. Please try again.',
        color: 'red',
      });
    },
  });
};

export const useExportReport = () => {
  return useMutation({
    mutationFn: async (options: {
      report_id: string;
      format: ExportOptions['format'];
      options?: ExportOptions;
    }) => {
      const response = await reportService.exportReport(options.report_id, {
        format: options.format,
        ...options.options
      });
      return response;
    },
    onSuccess: (_data) => {
      notifications.show({
        title: 'Export Successful',
        message: `Report exported successfully`,
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Export Failed',
        message: 'Failed to export report. Please try again.',
        color: 'red',
      });
    },
  });
};

// Additional hooks for specific report types
export const useActivityReport = (schoolId?: string, dateRange?: [Date, Date]) => {
  const filters: ReportFilters = {
    report_type: 'activities',
    start_date: dateRange?.[0]?.toISOString().split('T')[0],
    end_date: dateRange?.[1]?.toISOString().split('T')[0],
    filters: schoolId ? { school_id: schoolId } : undefined,
  };
  
  return useReports(filters);
};

export const useIncidentReport = (schoolId?: string, dateRange?: [Date, Date]) => {
  const filters: ReportFilters = {
    report_type: 'incidents',
    start_date: dateRange?.[0]?.toISOString().split('T')[0],
    end_date: dateRange?.[1]?.toISOString().split('T')[0],
    filters: schoolId ? { school_id: schoolId } : undefined,
  };
  
  return useReports(filters);
};

export const useAttendanceReport = (schoolId?: string, dateRange?: [Date, Date]) => {
  const filters: ReportFilters = {
    report_type: 'attendance',
    start_date: dateRange?.[0]?.toISOString().split('T')[0],
    end_date: dateRange?.[1]?.toISOString().split('T')[0],
    filters: schoolId ? { school_id: schoolId } : undefined,
  };
  
  return useReports(filters);
};